import { makeAutoObservable, runInAction } from 'mobx';
import { uniqBy } from 'lodash-es';
import { v4 } from 'uuid';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, IndicatorDto } from '@src/api';

export type IndicatorDtoDefId = Omit<IndicatorDto, 'id'> & {
  id: string;
  reportGroupCode?: string;
  reportGroupName?: string;
  reportVersionName?: string;
  reportTypeCode?: string;
  reportTypeName?: string;
  reportCodeExt?: string;
};

export type Filters = {
  reportGroup?: string;
  reportVersion?: string;
};

class IndicatorsStore {
  indicators: IndicatorDtoDefId[] = [];

  uniqByVarCodeIndicators: IndicatorDtoDefId[] = [];

  api: CmApi;

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: (IndicatorDtoDefId & { key?: string }) | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: IndicatorDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.indicators.getIndicators();

    runInAction(() => {
      const out = (data ?? []).map((item) => ({ ...item, id: v4() }));
      this.indicators = out;
      this.uniqByVarCodeIndicators = uniqBy(out, 'varCode'); // в таблице и дереве выводим только уникальные по varCode
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  groupDelete = async (indicators: IndicatorDto[]) => {
    preloaderStore.preloader(true, 'удаление...');
    const out = this.indicators.filter((indicator) => indicators.some((el) => el.varCode === indicator.varCode)); // в таблице только уникальные varCode, удаляем все записи с пришедшими varCode

    const promises = out.map((item) => this.api.indicators.deleteIndicatorsByCode(item.varCode ?? ''));
    await Promise.all(promises);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
  };
}

export const indicatorsStore = new IndicatorsStore({ api: cmApi });
